import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, KpiCard } from "@/components/Bits";
import { inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview, DocHeader } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, BUSINESS_TYPE_LABEL } from "@/lib/format";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];

export default function FinanceReports() {
  const { can, user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [view, setView] = useState("month-end");
  const [pack, setPack] = useState(null);
  const [gst, setGst] = useState(null);
  const [print, setPrint] = useState(false);
  const [business, setBusiness] = useState(null);
  const [settings, setSettings] = useState({});

  const saveSettings = async () => {
    try {
      await api.put("/gst/settings", settings);
      toast.success("GST configuration saved.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    setPack(null); setGst(null);
    try {
      const [m, g] = await Promise.all([
        api.get("/finance/month-end", { params: { month, year } }),
        can("gst.reports.view") ? api.get("/gst/report", { params: { month, year } })
          : Promise.resolve({ data: null }),
      ]);
      setPack(m.data); setGst(g.data);
      if (g.data?.settings) setSettings(g.data.settings);
    } catch (e) { toast.error(apiError(e)); }
  }, [month, year, can]);

  useEffect(() => {
    if (!can("finance.reports.view")) return;
    load();
    api.get("/settings/full").then(({ data }) => setBusiness(data.business || null)).catch(() => {});
  }, [load, can]);

  if (!can("finance.reports.view")) return <NoAccess what="financial reports" />;

  const download = async (fmt) => {
    try {
      const res = await api.get("/finance/month-end/export", { params: { month, year, fmt },
        responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `month-end-${year}-${String(month).padStart(2, "0")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="finance-reports-page">
      <PageHeader overline="Business" title="Month-end &amp; GST"
        subtitle={`Payroll, expenses, referrals, sales, purchases and GST preparation for ${
          BUSINESS_TYPE_LABEL[user?.business_type] || "your business"}.`} />

      <div className="mb-6 flex flex-wrap items-end gap-3">
        {[["month-end", "Month-end pack"], ["gst", "GST preparation"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`finance-view-${v}`}
            className={`rounded-full border px-5 py-2 text-sm font-medium ${view === v
              ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
            {l}
          </button>
        ))}
        <select className={`${inputCls} sm:!w-44`} value={month} data-testid="finance-month-select"
          onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" className={`${inputCls} sm:!w-28`} value={year} data-testid="finance-year-input"
          onChange={(e) => setYear(Number(e.target.value))} />
        <button onClick={() => setPrint(true)} className={ghostBtnCls} data-testid="finance-print-btn">Print</button>
        <button onClick={() => download("csv")} className={ghostBtnCls} data-testid="finance-csv-btn">CSV</button>
        <button onClick={() => download("xlsx")} className={primaryBtnCls} data-testid="finance-excel-btn">Excel</button>
      </div>

      {!pack ? <Loading /> : view === "month-end" ? (
        <div className="space-y-8" data-testid="month-end-panel">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Revenue" value={inr(pack.summary.revenue)} testid="kpi-revenue" />
            <KpiCard label="Outgo" value={inr(pack.summary.outgo)} testid="kpi-outgo" />
            <KpiCard label="Surplus" value={inr(pack.summary.surplus)} tone="accent" testid="kpi-surplus" />
            <KpiCard label="GST payable" value={inr(pack.gst.payable)} testid="kpi-gst-payable" />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="panel p-5">
              <p className="overline mb-4 text-[#EA580C]">Payroll</p>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <p>Staff paid: <b data-testid="pack-payroll-staff">{pack.payroll.totals.staff_count ?? 0}</b></p>
                <p>Net salary: <b data-testid="pack-payroll-net">{inr(pack.payroll.totals.net || 0)}</b></p>
                <p>Paid: <b>{inr(pack.payroll.totals.paid || 0)}</b></p>
                <p>Unpaid: <b>{inr(pack.payroll.totals.unpaid || 0)}</b></p>
              </div>
              {Object.keys(pack.payroll.by_method || {}).length > 0 && (
                <p className="mt-4 text-xs text-slate-500">
                  By method: {Object.entries(pack.payroll.by_method)
                    .map(([k, v]) => `${k} ${inr(v)}`).join(" · ")}
                </p>
              )}
              {pack.payroll.locked && <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-emerald-700">Month closed</p>}
            </div>

            <div className="panel p-5">
              <p className="overline mb-4 text-[#EA580C]">Expenses</p>
              <p className="text-sm">Total: <b data-testid="pack-expense-total">{inr(pack.expenses.total)}</b> across {pack.expenses.count} entries</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                {Object.entries(pack.expenses.by_category).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g, " ")}</span><span>{inr(v)}</span></li>
                ))}
                {Object.keys(pack.expenses.by_category).length === 0 && <li>No expenses recorded.</li>}
              </ul>
            </div>

            <div className="panel p-5">
              <p className="overline mb-4 text-[#EA580C]">Sales &amp; purchases</p>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <p>Pharmacy sales: <b data-testid="pack-pharmacy-sales">{inr(pack.sales.pharmacy_total)}</b> ({pack.sales.pharmacy_count})</p>
                <p>Hospital billing: <b data-testid="pack-hospital-sales">{inr(pack.sales.hospital_total)}</b> ({pack.sales.hospital_count})</p>
                <p>Purchases: <b data-testid="pack-purchases">{inr(pack.purchases.total)}</b> ({pack.purchases.count})</p>
              </div>
            </div>

            <div className="panel p-5">
              <p className="overline mb-4 text-[#EA580C]">Referrals</p>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <p>Earned: <b data-testid="pack-referral-total">{inr(pack.referrals.total)}</b></p>
                <p>Paid: <b>{inr(pack.referrals.paid)}</b></p>
                <p>Unpaid: <b>{inr(pack.referrals.unpaid)}</b></p>
                <p>Entries: <b>{pack.referrals.count}</b></p>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">{pack.gst.disclaimer}</p>
        </div>
      ) : !gst ? <Loading /> : (
        <div className="space-y-8" data-testid="gst-panel">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="gst-disclaimer">
            {gst.disclaimer} GSTIN on record: <b>{gst.gstin || "not set"}</b>.
          </div>

          <div className="panel p-5" data-testid="gst-settings-panel">
            <p className="overline mb-4 text-[#EA580C]">GST configuration ({gst.business_type})</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="text-sm"><span className="overline block text-slate-400">GSTIN</span>
                <input className={`${inputCls} mt-2`} value={settings.gstin || ""} data-testid="gst-gstin-input"
                  onChange={(e) => setSettings({ ...settings, gstin: e.target.value.toUpperCase() })} /></label>
              <label className="text-sm"><span className="overline block text-slate-400">Filing frequency</span>
                <select className={`${inputCls} mt-2`} value={settings.filing_frequency || "monthly"}
                  data-testid="gst-frequency-select"
                  onChange={(e) => setSettings({ ...settings, filing_frequency: e.target.value })}>
                  <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                </select></label>
              {[["gst_registered", "GST registered"],
                ["composition_scheme", "Composition scheme"],
                ["pharmacy_sales_taxable", "Pharmacy sales are taxable"],
                ["hospital_services_taxable", "Hospital services are taxable"]].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={Boolean(settings[key])} data-testid={`gst-${key}-toggle`}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
            {can("settings.manage") && (
              <button onClick={saveSettings} className={`${primaryBtnCls} mt-5`} data-testid="gst-save-settings-btn">
                Save GST configuration
              </button>
            )}
            <p className="mt-3 text-xs text-slate-500">
              Nothing is assumed to be taxable — these switches decide what appears in the reports.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Output GST (GSTR-1)" value={inr(gst.gstr1.tax)} testid="kpi-output-gst" />
            <KpiCard label="Input GST (GSTR-2 / 2B)" value={inr(gst.gstr2.tax)} testid="kpi-input-gst" />
            <KpiCard label="Net payable" value={inr(gst.net.payable)} tone="accent" testid="kpi-net-payable" />
            <KpiCard label="Credit carried" value={inr(gst.net.credit_carried)} testid="kpi-credit-carried" />
          </div>

          <div>
            <p className="overline mb-3 text-[#EA580C]">Outward supplies — GSTR-1 preparation</p>
            <Table testid="gstr1-table"
              columns={["Date", "Number", "Party", "Taxable", "CGST", "SGST", "IGST", "Total"]}
              rows={gst.gstr1.rows}
              empty={<p className="p-6 text-center text-sm text-slate-500">No taxable outward supplies this month.</p>}
              renderRow={(r, i) => (
                <tr key={`${r.number}-${i}`} data-testid={`gstr1-row-${i}`}>
                  <Td className="whitespace-nowrap text-slate-500">{dmy(r.date)}</Td>
                  <Td className="font-mono-num text-slate-700">{r.number}</Td>
                  <Td className="text-slate-600">{r.party || "—"}</Td>
                  <Td>{inr(r.taxable)}</Td><Td>{inr(r.cgst)}</Td><Td>{inr(r.sgst)}</Td>
                  <Td>{inr(r.igst)}</Td><Td className="font-medium">{inr(r.total)}</Td>
                </tr>
              )} />
          </div>

          <div>
            <p className="overline mb-3 text-[#EA580C]">
              Inward supplies — GSTR-2 / 2B reconciliation ({gst.gstr2.matched} with GSTIN,
              {" "}{gst.gstr2.unmatched} without)
            </p>
            <Table testid="gstr2-table"
              columns={["Date", "Number", "Party", "GSTIN", "Taxable", "Tax", "Total", "Reconcilable"]}
              rows={gst.gstr2.rows}
              empty={<p className="p-6 text-center text-sm text-slate-500">No inward supplies with GST this month.</p>}
              renderRow={(r, i) => (
                <tr key={`${r.number}-${i}`} data-testid={`gstr2-row-${i}`}>
                  <Td className="whitespace-nowrap text-slate-500">{dmy(r.date)}</Td>
                  <Td className="font-mono-num text-slate-700">{r.number}</Td>
                  <Td className="text-slate-600">{r.party || "—"}</Td>
                  <Td className="text-xs text-slate-500">{r.gstin || "—"}</Td>
                  <Td>{inr(r.taxable)}</Td><Td>{inr(r.tax)}</Td><Td className="font-medium">{inr(r.total)}</Td>
                  <Td className={r.reconciled ? "text-emerald-700" : "text-amber-700"}>
                    {r.reconciled ? "Yes" : "No GSTIN"}
                  </Td>
                </tr>
              )} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="panel p-5"><p className="overline text-[#EA580C]">Credit notes (sales returns)</p>
              <p className="mt-2 text-sm">{gst.credit_notes.count} notes · {inr(gst.credit_notes.amount)}</p></div>
            <div className="panel p-5"><p className="overline text-[#EA580C]">Debit notes (purchase returns)</p>
              <p className="mt-2 text-sm">{gst.debit_notes.count} notes · {inr(gst.debit_notes.amount)}</p></div>
          </div>
        </div>
      )}

      <PrintPreview open={print} onClose={() => setPrint(false)}
        title={`Month-end summary ${MONTHS[month - 1]} ${year}`} testid="finance-print-preview">
        {pack && (
          <>
            <DocHeader business={business} docTitle="Monthly Financial Summary"
              right={<p className="text-[11px]">{MONTHS[month - 1]} {year}</p>} />
            <table className="mt-6 w-full border-collapse text-[12px]">
              <tbody>
                {[["Pharmacy sales", pack.sales.pharmacy_total], ["Hospital billing", pack.sales.hospital_total],
                  ["Total revenue", pack.summary.revenue], ["Purchases", pack.purchases.total],
                  ["Salaries (net)", pack.payroll.totals.net || 0], ["Expenses", pack.expenses.total],
                  ["Referral payouts", pack.referrals.total], ["Total outgo", pack.summary.outgo],
                  ["Output GST", pack.gst.output_gst], ["Input GST", pack.gst.input_gst],
                  ["GST payable", pack.gst.payable]].map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-200">
                    <td className="py-1.5">{k}</td>
                    <td className="py-1.5 text-right">{inr(v)}</td>
                  </tr>
                ))}
                <tr className="border-y-2 border-slate-800 font-ui text-[14px] font-bold">
                  <td className="py-1.5">Surplus</td>
                  <td className="py-1.5 text-right">{inr(pack.summary.surplus)}</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-8 text-[10px] text-slate-500">{pack.gst.disclaimer}</p>
          </>
        )}
      </PrintPreview>
    </div>
  );
}
